/**
 * PublicFooter.js — Footer condiviso del sito pubblico di un noleggio.
 *
 * Usato da PublicCarList (home), ContactPage e PrivacyPage (modalità
 * azienda) così il footer — e il branding "Servizio offerto da
 * NoleggioinCloud" — vive in un solo posto invece di essere copiato tre
 * volte con variazioni leggere che nel tempo divergono (come accadeva
 * prima: tre footer leggermente diversi tra loro).
 *
 * Non introduce nessun nuovo campo di personalizzazione lato admin: usa
 * solo dati già esistenti in `settings.sito` (nomeSito, slogan,
 * footerSottotitolo, coloreAccento) e `settings.azienda` (ragioneSociale,
 * piva, indirizzo, email, telefono) passati dal genitore, quindi i
 * pannelli "Il Tuo Sito" e "Dati Azienda" restano esattamente come sono.
 *
 * Dati legali obbligatori (D.Lgs. 70/2003 art. 7 — obblighi informativi
 * per i prestatori di servizi della società dell'informazione, e Codice
 * del Consumo): ragione sociale, P.IVA e sede vengono mostrati sotto il
 * brand del noleggio, quando l'azienda li ha compilati in "Dati Azienda".
 *
 * Il link "Servizio offerto da NoleggioinCloud" punta a
 * https://www.noleggioincloud.app (direttamente su www: evita il redirect e
 * dà il valore del link — un backlink per ogni sito cliente — alla pagina giusta).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Car } from 'lucide-react';
import platformLogo from '../../assets/logo-full-transparent.webp'; // Audit 2026-09-20, punto #20: WebP lossless invece di PNG

const PublicFooter = ({
  displayName,
  slogan,
  logoSito,
  footerSottotitolo,
  accentColor = '#2563EB',
  basePath = '',
  // Dati legali del noleggio (ragione sociale, P.IVA, sede) — obbligatori
  // per legge (D.Lgs. 70/2003 art. 7, Codice del Consumo) per un sito che
  // offre servizi/vendite online. Facoltativi qui: se l'azienda non li ha
  // ancora compilati nel pannello "Dati Azienda" semplicemente non vengono
  // mostrati, senza rompere il footer per i siti già esistenti.
  ragioneSociale,
  piva,
  indirizzo,
  email,
  telefono,
}) => {
  const year = new Date().getFullYear();
  // Se ragioneSociale coincide col nome mostrato non ha senso ripeterla.
  const showRagioneSociale = ragioneSociale && ragioneSociale !== displayName;
  const hasLegalInfo = showRagioneSociale || piva || indirizzo || email || telefono;

  return (
    <footer className="bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-8">
          {/* Brand del noleggio */}
          <div className="flex items-start gap-3">
            {logoSito ? (
              <img src={logoSito} alt={displayName} className="h-11 w-auto max-w-[130px] object-contain flex-shrink-0" />
            ) : (
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${accentColor}1A`, color: accentColor }}
              >
                <Car className="w-5 h-5" />
              </div>
            )}
            <div>
              <p className="font-display font-extrabold text-slate-900 tracking-tight">{displayName}</p>
              {(slogan || footerSottotitolo) && (
                <p className="text-sm text-slate-500 mt-0.5 max-w-xs">{footerSottotitolo || slogan}</p>
              )}
              {hasLegalInfo && (
                <p className="text-xs text-slate-400 mt-2 max-w-xs leading-relaxed">
                  {showRagioneSociale && <>{ragioneSociale}</>}
                  {showRagioneSociale && piva && <> · </>}
                  {piva && <>P.IVA {piva}</>}
                  {(showRagioneSociale || piva) && indirizzo && <><br /></>}
                  {indirizzo}
                  {(indirizzo && (email || telefono)) && <><br /></>}
                  {email}
                  {email && telefono && <> · </>}
                  {telefono}
                </p>
              )}
            </div>
          </div>

          {/* Link rapidi */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <Link to={basePath || '/'} className="text-slate-500 hover:text-slate-900 font-semibold transition" style={{ textDecoration: 'none' }}>
              Catalogo
            </Link>
            <Link to={`${basePath}/contatti`} className="text-slate-500 hover:text-slate-900 font-semibold transition" style={{ textDecoration: 'none' }}>
              Contatti
            </Link>
            <Link to={`${basePath}/account`} className="text-slate-500 hover:text-slate-900 font-semibold transition" style={{ textDecoration: 'none' }}>
              Area cliente
            </Link>
            <Link to={`${basePath}/privacy`} className="text-slate-500 hover:text-slate-900 font-semibold transition" style={{ textDecoration: 'none' }}>
              Privacy
            </Link>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-400">
            © {year} {displayName}. Tutti i diritti riservati.
          </p>
          <a
            href="https://www.noleggioincloud.app"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition"
            style={{ textDecoration: 'none' }}
          >
            Servizio offerto da
            <img src={platformLogo} alt="NoleggioinCloud" className="h-3.5 w-auto opacity-80" />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
